/**
 * modalStack — one stacking authority for every layered surface (MFG-PR-3).
 *
 * Problems this solves (all pre-dating PR #407):
 * 1. Mixed z systems: AppModal hardcoded z-100 while Radix dialogs
 *    (ConfirmDialog) sit at z-50 — a confirmation opened from inside an
 *    action modal rendered BEHIND it.
 * 2. ESC closed the OLDEST modal: every FocusTrap listens on document in
 *    the capture phase and stops propagation, so the first-registered
 *    listener won. Layers now ask `isTopModalLayer` before closing.
 * 3. Body scroll lock used save/restore of `overflow`, which breaks on
 *    non-LIFO closes of stacked modals — replaced by a ref-count.
 *
 * Z bands (documented contract):
 *   90        — full-screen workspaces/drawers (portaled, below modals)
 *   100 + 10n — AppModal instances, ascending: newest always on top
 *   400       — Radix dialogs (ConfirmDialog): the terminal confirmation
 *               layer, always above any AppModal
 */

export const WORKSPACE_Z = 90;
export const MODAL_BASE_Z = 100;
export const MODAL_STEP_Z = 10;

let nextId = 0;
const stack = [];

export function pushModalLayer() {
  nextId += 1;
  const id = nextId;
  stack.push(id);
  return { id, zIndex: MODAL_BASE_Z + (stack.length - 1) * MODAL_STEP_Z };
}

export function popModalLayer(id) {
  const index = stack.indexOf(id);
  if (index !== -1) stack.splice(index, 1);
}

export function isTopModalLayer(id) {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

export function modalStackDepth() {
  return stack.length;
}

// ── Body scroll lock (ref-counted — safe for stacked + non-LIFO closes) ──
let lockCount = 0;
let savedOverflow = '';

export function lockBodyScroll() {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}

export function unlockBodyScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = savedOverflow;
  }
}
